import { useContext, useCallback } from 'react';
import LanguageContext from './LanguageContext';
import en from './translations/en';
import de from './translations/de';

const translations = { en, de };

/**
 * Get a nested value from an object using dot notation
 * @param {object} obj - Object to traverse
 * @param {string} path - Dot-separated path (e.g., 'common.save')
 * @returns {*} Value at path or undefined
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Interpolate variables in a string template
 * Replaces {variable} placeholders with values from params
 * @param {string} template - String with {variable} placeholders
 * @param {object} params - Object with variable values
 * @returns {string} Interpolated string
 */
function interpolate(template, params = {}) {
  if (typeof template !== 'string') return template;
  
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return params[key] !== undefined ? params[key] : match;
  });
}

/**
 * Hook for accessing translations and locale
 * @returns {object} Translation utilities
 */
export function useTranslation() {
  const context = useContext(LanguageContext);
  
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  
  const { language, locale, setLanguage, supportedLanguages } = context;
  
  /**
   * Get a translated string by key
   * Falls back to English if translation not found
   * @param {string} key - Dot-notation key (e.g., 'common.save')
   * @param {object} [params] - Variables for interpolation
   * @returns {string|Array} Translated and interpolated value
   */
  const t = useCallback((key, params) => {
    // Try current language first
    let value = getNestedValue(translations[language], key);
    
    // Fall back to English if not found
    if (value === undefined && language !== 'en') {
      value = getNestedValue(translations.en, key);
    }
    
    // Return key if nothing found (for debugging)
    if (value === undefined) {
      console.warn(`Translation missing: ${key}`);
      return key;
    }
    
    // Handle arrays (don't interpolate, return as-is)
    if (Array.isArray(value)) {
      return params ? value.map(item => interpolate(item, params)) : value;
    }
    
    // Interpolate string values
    return interpolate(value, params);
  }, [language]);

  /**
   * Format a date according to the current locale
   * @param {Date|string} date - Date to format
   * @param {object} [options] - Intl.DateTimeFormat options
   * @returns {string} Formatted date string
   */
  const formatDate = useCallback((date, options = {}) => {
    const dateObj = typeof date === 'string' ? new Date(date + 'T00:00:00') : date;
    
    const defaultOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };
    
    return dateObj.toLocaleDateString(locale, { ...defaultOptions, ...options });
  }, [locale]);

  /**
   * Format a date range according to the current locale
   * @param {Date|string} startDate - Start date
   * @param {Date|string} endDate - End date
   * @returns {string} Formatted date range
   */
  const formatDateRange = useCallback((startDate, endDate) => {
    const start = typeof startDate === 'string' ? new Date(startDate + 'T00:00:00') : startDate;
    const end = typeof endDate === 'string' ? new Date(endDate + 'T00:00:00') : endDate;
    
    // Same day
    if (start.getTime() === end.getTime()) {
      return formatDate(start);
    }
    
    // Same year - omit year from start date
    if (start.getFullYear() === end.getFullYear()) {
      const startStr = start.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
      const endStr = formatDate(end);
      return `${startStr} - ${endStr}`;
    }
    
    // Different years - show full dates
    return `${formatDate(start)} - ${formatDate(end)}`;
  }, [locale, formatDate]);

  return {
    t,
    language,
    locale,
    setLanguage,
    supportedLanguages,
    formatDate,
    formatDateRange,
  };
}

export default useTranslation;
